import type { Point, Stroke } from "./types";
import { clear, segment } from "./render";

/** signed.tsx divides every scheduled offset by this. Keep them in step. */
export const SITE_SPEED_FACTOR = 2;

/**
 * Flattens strokes into a single timeline ordered by timestamp, exactly as
 * signed.tsx does. Sorting globally (rather than per stroke) matters: it is what
 * makes overlapping or out-of-order strokes replay in the order they were drawn.
 */
export function timeline(strokes: Stroke[]): Point[] {
  return strokes.flatMap((s) => s.points).sort((a, b) => a.timestamp - b.timestamp);
}

export interface Playback {
  cancel(): void;
}

/**
 * Replays a signature at the site's timing. `onDone` fires once the last point
 * lands. Returns a handle so a new playback (or a fresh stroke) can pre-empt it.
 */
export function play(
  ctx: CanvasRenderingContext2D,
  strokes: Stroke[],
  speedFactor: number,
  onDone: () => void,
): Playback {
  const points = timeline(strokes);
  if (points.length === 0) {
    onDone();
    return { cancel: () => {} };
  }

  const startTime = points[0].timestamp;
  const begin = performance.now();
  let index = 0;
  let frame = 0;

  clear(ctx);

  const step = () => {
    const elapsed = performance.now() - begin;

    while (index < points.length) {
      const point = points[index];
      if (elapsed < (point.timestamp - startTime) / speedFactor) break;

      const prev = points[index - 1];
      // Only join points from the same pen-down; otherwise the replay draws a
      // phantom line across every pen-lift.
      if (prev && prev.strokeId === point.strokeId) segment(ctx, prev, point);
      index++;
    }

    if (index < points.length) {
      frame = requestAnimationFrame(step);
    } else {
      onDone();
    }
  };

  frame = requestAnimationFrame(step);
  return { cancel: () => cancelAnimationFrame(frame) };
}
